<template>
    <div class="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <div role="tablist" class="tabs tabs-lifted">
            <a role="tab" class="tab" :class="{ 'tab-active': currentTab === 'schema' }" @click="currentTab = 'schema'">Schema</a>
            <a role="tab" class="tab" :class="{ 'tab-active': currentTab === 'query-factory' }" @click="currentTab = 'query-factory'">Query Factory</a>
        </div>

        <div v-show="currentTab === 'schema'" class="bg-base-100 p-4 rounded-b-lg rounded-tr-lg shadow-inner">
            <div class="flex justify-between items-center mb-4">
                <h2 class="text-xl font-bold text-gray-800 dark:text-gray-200">Database Schema</h2>
                <button v-if="allowTableEdit === true" class="btn btn-sm" @click="addTable">
                    <Icon icon="tabler:table-plus" class="mr-2"/>
                    Add Table
                </button>
            </div>

            <div v-if="localSchema.length === 0" class="text-center py-6 border-2 border-dashed rounded-lg">
                <p class="text-gray-500">No tables defined for this application.</p>
            </div>

            <div v-else class="space-y-4">
                <div v-for="(table, tableIndex) in localSchema" :key="tableIndex" class="p-4 border rounded-lg bg-white dark:bg-gray-700 shadow-sm">
                    <div class="flex justify-between items-center mb-3">
                        <input type="text" v-model="table.name" placeholder="Table Name" class="input input-bordered input-sm font-semibold" :disabled="allowTableEdit === false">
                        <div>
                            <button class="btn btn-xs btn-info btn-ghost" @click="viewTableData(table.name)">View Data</button>
                            <button v-if="allowTableEdit === true" class="btn btn-xs btn-error btn-ghost" @click="removeTable(tableIndex)">Remove Table</button>
                        </div>
                    </div>
                    
                    <div class="grid grid-cols-1 md:grid-cols-3 text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 px-2 grid-space-2">
                        <span>Column Name</span>
                        <span>Data Type</span>
                        <span v-if="allowTableEdit === true">Actions</span>
                    </div>

                    <div v-for="(column, colIndex) in table.columns" :key="colIndex" class="flex items-center space-x-2 p-2 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600">
                        <input type="text" v-model="column.name" placeholder="Column Name" class="input input-bordered input-xs flex-grow" :disabled="allowTableEdit === false">
                        <select v-model="column.type" class="select select-bordered select-xs" :disabled="allowTableEdit === false">
                            <option>Text</option>
                            <option>Number</option>
                            <option>Date</option>
                            <option>Boolean</option>
                        </select>
                        <button v-if="allowTableEdit === true" class="btn btn-xs btn-error btn-ghost" @click="removeColumn(tableIndex, colIndex)">
                            <Icon icon="tabler:trash" />
                        </button>
                    </div>

                    <button v-if="allowTableEdit === true" class="btn btn-xs btn-outline mt-2" @click="addColumn(tableIndex)">
                        <Icon icon="tabler:plus" class="mr-1"/> Add Column
                    </button>
                </div>
            </div>

            <div v-if="allowTableEdit === true" class="mt-6 flex justify-end">
                <button class="btn" @click="saveSchema" :disabled="isSaving === true">
                    <span v-if="isSaving === true" class="loading loading-spinner loading-sm mr-2"></span>
                    Save Schema
                </button>
            </div>
        </div>
        
        <div v-show="currentTab === 'query-factory'" class="bg-base-100 p-4 rounded-b-lg rounded-tr-lg shadow-inner">
            <h2 class="text-xl font-bold text-gray-800 dark:text-gray-200 mb-4">Query Factory</h2>
            
            <div class="grid grid-cols-1 md:grid-cols-2 grid-space-6">
                <div>
                    <h3 class="font-semibold mb-2">Build Query</h3>
                    <div class="p-4 border rounded-lg bg-white dark:bg-gray-700 space-y-3">
                        <select v-model="selectedQueryTable" class="select select-bordered w-full">
                            <option :value="null" disabled>Select a table</option>
                            <option v-for="table in schema" :key="table.name" :value="table.name">{{ table.name }}</option>
                        </select>

                        <div v-if="selectedQueryTable !== null && selectedQueryTable !== undefined && selectedQueryTable !== ''">
                            <h4 class="font-medium text-sm mb-2">Filters</h4>
                            <div v-for="(filter, index) in activeFilters" :key="index" class="flex items-center space-x-2 mb-2 p-2 bg-gray-50 dark:bg-gray-600 rounded">
                                <span class="text-sm font-mono flex-grow">{{ filter.column }} {{ formatFilterOperator(filter.operator) }} "{{ filter.value }}"</span>
                                <button @click="removeFilter(index)" class="btn btn-xs btn-ghost btn-error">
                                    <Icon icon="tabler:x" />
                                </button>
                            </div>
                            
                            <div class="flex items-end space-x-2 border-t pt-3">
                                <div class="form-control">
                                    <label class="label pb-1"><span class="label-text">Column</span></label>
                                    <select v-model="newFilter.column" class="select select-bordered select-sm">
                                        <option v-for="col in queryableColumns" :key="col.name" :value="col.name">{{ col.name }}</option>
                                    </select>
                                </div>
                                <div class="form-control">
                                     <label class="label pb-1"><span class="label-text">Operator</span></label>
                                    <select v-model="newFilter.operator" class="select select-bordered select-sm">
                                        <option v-for="op in filterOperators" :key="op" :value="op">{{ formatFilterOperator(op) }}</option>
                                    </select>
                                </div>
                                <div class="form-control flex-grow">
                                     <label class="label pb-1"><span class="label-text">Value</span></label>
                                    <input type="text" v-model="newFilter.value" placeholder="Filter value" class="input input-bordered input-sm">
                                </div>
                                <button @click="addFilter" class="btn btn-sm" :disabled="newFilter.column === undefined || newFilter.column === null || newFilter.column === ''">Add</button>
                            </div>
                        </div>
                    </div>
                    <div class="mt-4">
                        <button @click="executeQuery" class="btn w-full" :disabled="(selectedQueryTable === undefined || selectedQueryTable === null || selectedQueryTable === '') || isQuerying === true">
                            <span v-if="isQuerying === true" class="loading loading-spinner"></span>
                            Execute Query
                        </button>
                    </div>
                </div>

                <div>
                    <h3 class="font-semibold mb-2">Results</h3>
                    <div class="p-4 border rounded-lg bg-white dark:bg-gray-700 min-h-[200px]">
                        <div v-if="isQuerying === true" class="flex justify-center items-center h-full">
                            <span class="loading loading-lg loading-dots"></span>
                        </div>
                        <div v-else class="overflow-x-auto">
                           <table v-if="queryResults.length > 0" class="table table-sm">
                                <thead>
                                    <tr>
                                        <th v-for="key in Object.keys(queryResults[0])" :key="key">{{ key }}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(row, index) in queryResults" :key="index">
                                        <td v-for="value in row" :key="value"><pre class="text-xs">{{ value }}</pre></td>
                                    </tr>
                                </tbody>
                            </table>
                            <p v-else class="text-center text-gray-500 py-8">No results to display. Run a query to see data here.</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <dialog :open="isViewDataModalOpen === true" class="modal">
            <div class="modal-box w-11/12 max-w-4xl">
                <h3 class="font-bold text-lg">Data in "{{ viewingTableName }}"</h3>
                <button @click="isViewDataModalOpen = false" class="btn btn-sm btn-circle btn-ghost absolute right-2 top-2">✕</button>
                <div class="py-4 overflow-x-auto">
                    <table v-if="tableData.length > 0" class="table table-sm">
                        <thead>
                            <tr>
                                <th v-for="key in Object.keys(tableData[0])" :key="key">{{ key }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(row, index) in tableData" :key="index">
                                <td v-for="key in Object.keys(row)" :key="key">
                                    <pre class="text-xs">{{ JSON.stringify(row[key], null, 2) }}</pre>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <p v-else class="text-center text-gray-500">No data in this table yet.</p>
                </div>
            </div>
            <form method="dialog" class="modal-backdrop">
                <button @click="isViewDataModalOpen = false">close</button>
            </form>
        </dialog>
    </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue';
import { useToast } from 'vue-toastification';
import { Icon } from '@iconify/vue/dist/offline';
import axios from '@/api/axiosInstance';
import { globalState } from '@/globalState';
import { useRouter } from 'vue-router';

const props = defineProps({
    applicationId: {
        type: String,
        required: true
    },
    schema: {
        type: Array,
        default: () => []
    },
    allowTableEdit: {
        type: Boolean,
        default: false
    }
});

const toast = useToast();
const router = useRouter();
const localSchema = ref([]);
const isSaving = ref(false);
const isViewDataModalOpen = ref(false);
const viewingTableName = ref('');
const tableData = ref([]);

const currentTab = ref('schema');

const selectedQueryTable = ref(null);
const activeFilters = ref([]);
const newFilter = ref({ column: '', operator: 'equals', value: '' });
const queryResults = ref([]);
const isQuerying = ref(false);
const filterOperators = ['equals', 'not_equals', 'contains', 'greater_than', 'less_than', 'starts_with', 'ends_with'];
const filterOperatorLabels = {
    equals: 'Equals',
    not_equals: 'Not equals',
    contains: 'Contains',
    greater_than: 'Greater than',
    less_than: 'Less than',
    starts_with: 'Starts with',
    ends_with: 'Ends with'
};

function formatFilterOperator(operator) {
    if (typeof operator !== 'string' || operator === '') {
        return '';
    }
    return typeof filterOperatorLabels[operator] === 'string' ? filterOperatorLabels[operator] : operator.replaceAll('_', ' ');
}

const queryableColumns = computed(() => {
    if (selectedQueryTable.value === undefined || selectedQueryTable.value === null || selectedQueryTable.value === '' || props.schema === undefined || props.schema === null || props.schema.length === 0) return [];
    const table = props.schema.find(t => t.name === selectedQueryTable.value);
    return table ? table.columns : [];
});

watch(selectedQueryTable, (newTable) => {
    activeFilters.value = [];
    queryResults.value = [];
    newFilter.value = { column: '', operator: 'equals', value: '' };
    if (queryableColumns.value.length > 0) {
        newFilter.value.column = queryableColumns.value[0].name;
    }
});

function getAuthHeaders() {
  const token = document.cookie.split('; ').find(row => row.startsWith('authToken='))?.split('=')[1];
  const headers = {
    'Content-Type': 'application/json'
  };
  if (token !== undefined && token !== null && token !== '') {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

watch(() => props.schema, (newSchema) => {
    localSchema.value = JSON.parse(JSON.stringify(newSchema !== undefined && newSchema !== null && newSchema.length > 0 ? newSchema : []));
}, { immediate: true, deep: true });

const addTable = () => {
    localSchema.value.push({ name: '', columns: [{ name: '', type: 'Text' }] });
};

const removeTable = (index) => {
    localSchema.value.splice(index, 1);
};

const addColumn = (tableIndex) => {
    localSchema.value[tableIndex].columns.push({ name: '', type: 'Text' });
};

const removeColumn = (tableIndex, colIndex) => {
    localSchema.value[tableIndex].columns.splice(colIndex, 1);
};

const viewTableData = async (tableName) => {
    if (tableName !== undefined && tableName !== null && tableName !== '') {
        toast.error("Table name is missing.");
        return;
    }
    viewingTableName.value = tableName;
    isViewDataModalOpen.value = true;
    tableData.value = []; 
    try {
        const response = await axios.get(`${globalState.apiEndpoint}/api/appDB/${props.applicationId}/${tableName}`, {
            headers: getAuthHeaders()
        });
        tableData.value = response.data;
    } catch (error) {
        console.error(`Error fetching data for table ${tableName}:`, error);
        toast.error(error.response?.data?.error !== undefined && error.response?.data?.error !== null && error.response?.data?.error !== '' ? error.response.data.error : `Failed to fetch data for ${tableName}.`);
        isViewDataModalOpen.value = false;
    }
};

const addFilter = () => {
    if (newFilter.value.column === undefined || newFilter.value.column === null || newFilter.value.column === '' || newFilter.value.value === undefined || newFilter.value.value === null || newFilter.value.value === '') {
        toast.info("Please provide a value for the filter.");
        return;
    }
    activeFilters.value.push({ ...newFilter.value });
    newFilter.value.value = '';
};

const removeFilter = (index) => {
    activeFilters.value.splice(index, 1);
};

const executeQuery = async () => {
    if (selectedQueryTable.value === undefined || selectedQueryTable.value === null || selectedQueryTable.value === '') {
        toast.error("Please select a table to query.");
        return;
    }
    isQuerying.value = true;
    queryResults.value = [];
    try {
        const payload = {
            tableName: selectedQueryTable.value,
            filters: activeFilters.value
        };
        const response = await axios.post(`${globalState.apiEndpoint}/api/appDB/${props.applicationId}/query`, payload, {
            headers: getAuthHeaders()
        });
        queryResults.value = response.data;
        if (response.data.length === 0) {
            toast.info("Query executed successfully, but returned no results.");
        }
    } catch (error) {
        console.error("Error executing query:", error);
        toast.error(error.response?.data?.error !== undefined && error.response?.data?.error !== null && error.response?.data?.error !== '' ? error.response.data.error : "Failed to execute query.");
    } finally {
        isQuerying.value = false;
    }
};

const saveSchema = async () => {
    if (props.applicationId === undefined || props.applicationId === null || props.applicationId === '') {
        toast.error("Application ID is missing. Cannot save schema.");
        return;
    }

    isSaving.value = true;
    try {
        await axios.put(`${globalState.apiEndpoint}/api/appDB/${props.applicationId}/schema`, localSchema.value, {
            headers: getAuthHeaders()
        });
        toast.success("Database schema saved successfully!");
    } catch (error) {
        console.error("Error saving schema:", error);
        toast.error(error.response?.data?.error !== undefined && error.response?.data?.error !== null && error.response?.data?.error !== '' ? error.response.data.error : "Failed to save schema.");
    } finally {
        isSaving.value = false;
    }
};

</script> 